import React from 'react';
import { useTheme } from '@emotion/react';
import {
  type RestModalAction,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { type ModalActionHandler } from '@bbkFunnelComponents/modal';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import Icon from '@bbkFunnelUtils/exit-icon';

type Props = {
  mainAction: RestModalForwards;
  otherActions: RestModalForwards[];
  onClick: ModalActionHandler;
  name: string;
  intercomMessage: string;
};
export const ModalActions: React.FC<Props> = ({
  name,
  otherActions = [],
  mainAction,
  onClick,
  intercomMessage,
}) => {
  const theme = useTheme() as EmotionTheme;

  const otherActionsMap = new Map<
    RestModalAction[keyof RestModalAction],
    RestModalForwards
  >(otherActions.map((i) => [i.action, i]));

  const labelColor = theme.primaryColor;
  const labelStyle: React.CSSProperties = {
    textTransform: 'none',
    paddingRight: 0,
    fontSize: '16px',
    fontWeight: 400,
    color: labelColor,
    letterSpacing: '-0.08px',
  };
  const equalButtonSize = theme.complianceEqualButtonSize;

  const intercomAction = otherActionsMap.get('intercom_chat');
  const modalEmailAction = otherActionsMap.get('modal_email');
  const scheduleCallAction = otherActionsMap.get('schedule_call');
  return (
    <div className="modal-actions">
      <div className="MAContainer">
        <div className={cs('btn', name)}>
          <Icon name="directionalArrow" classes="directional-arrow" />
          <FunnelButton
            onClick={() => onClick(mainAction, { message: intercomMessage })}
            specificStyles={{
              height: '60px',
              boxShadow: '0 5px 10px 0 rgba(0,0,0,0.15)',
              borderRadius: '5px',
              width: '100%',
              backgroundColor: theme.primaryColor,
            }}
            classes="d-flex align-items-center justify-content-center"
          >
            <ActionButtonIcons
              icon="bbk-esi-chat"
              styles={{ fill: labelColor }}
            />
            &nbsp;
            <span
              style={{
                color: '#fff',
                textTransform: 'none',
                fontSize: '22px',
                letterSpacing: '-0.44px',
                textAlign: 'center',
                fontWeight: 500,
                borderRadius: '5px',
              }}
            >
              {mainAction.text}
            </span>
          </FunnelButton>
        </div>
        {otherActionsMap ? (
          <div
            className={cs('other-options', {
              'one-option': otherActionsMap.size === 1,
              'tw-flex tw-justify-center': equalButtonSize,
            })}
          >
            {/* Go through other options and put in */}
            {scheduleCallAction && (
              <FunnelButton
                onClick={() => onClick(scheduleCallAction)}
                specificStyles={{ backgroundColor: 'transparent' }}
                classes="d-flex align-items-center justify-content-center"
              >
                <ActionButtonIcons
                  icon="bbk-esi-phone"
                  styles={{ color: labelColor }}
                />
                &nbsp;
                <span style={labelStyle}>Schedule a call</span>
              </FunnelButton>
            )}
            {modalEmailAction && (
              <FunnelButton
                onClick={() => onClick(modalEmailAction, 'other_actions')}
                specificStyles={{ backgroundColor: 'transparent' }}
                classes="d-flex align-items-center justify-content-center"
              >
                <ActionButtonIcons
                  icon="bbk-esi-message"
                  styles={{ color: labelColor }}
                />
                &nbsp;
                <span style={labelStyle}>
                  {modalEmailAction.text || 'Email'}
                </span>
              </FunnelButton>
            )}
            {intercomAction && (
              <FunnelButton
                onClick={() => onClick(intercomAction)}
                specificStyles={{ backgroundColor: 'transparent' }}
                classes="d-flex align-items-center justify-content-center"
              >
                <ActionButtonIcons
                  icon="bbk-esi-chat"
                  styles={{ color: labelColor }}
                />
                &nbsp;
                <span style={labelStyle}>
                  {intercomAction.text || 'Chat now'}
                </span>
              </FunnelButton>
            )}
          </div>
        ) : (
          <div className="space-me" />
        )}
      </div>
    </div>
  );
};
